import {ApiService} from '@/api';
import {
  ActivityHistory,
  ActivityType,
  SearchFilter,
  WaitingActivityRequests,
  ResponseToActivityRequestsResponse,
  RedirectActivityRequestsResponse, ActivityHistoryStatusType,
} from '@/models';
import {GrantActivityToMultipleGeeks} from '@/models/admin-panel';
import {RecentActivity} from '@/models/dashboard';
import {GeeksSkillWithActivities} from '@/models/geek-profile';
import {
  GeekRewardingRequestData,
  GeekRewardingRequest,
  GeekRewardingRequestPayload,
} from '@/models/reward-geek';
import {Gratification} from '@/models/rules';

export const GEEKS_ACTIVITIES_HISTORY_LIMIT = 10;

export class ActivitiesApi {
  private readonly activitiesBaseUrl = '/api/activities';
  private readonly geeksBaseUrl = '/api/geeks';

  public async fetchAll(): Promise<ActivityType[]> {
    const response = await ApiService.get(`${this.activitiesBaseUrl}/types`);

    return response.json();
  }

  public async fetchGratifications(): Promise<Gratification[]> {

    const response = await ApiService.get(`${this.activitiesBaseUrl}/gratifications`);

    return response.json();
  }

  public async fetchRequests(
    offset: number = 0,
    limit: number = 10,
  ): Promise<GeekRewardingRequestData> {
    const response = await ApiService.get(
      `${this.activitiesBaseUrl}/requests`,
      {params: {offset, limit}},
    );
    const data: GeekRewardingRequest[] = await response.json();
    const totalCount: number = parseInt(response.headers.get('x-items-count') as string, 10);

    return new Promise((resolve) => resolve({data, totalCount}));
  }

  public async postActivityRequest(requestPayload: GeekRewardingRequestPayload): Promise<void> {
    await ApiService.post(`${this.activitiesBaseUrl}/requests`, requestPayload);
  }

  public async fetchGeekActivities(seasonId: string, geekId: string): Promise<GeeksSkillWithActivities[]> {
    const response = await ApiService.get(`${this.geeksBaseUrl}/${geekId}/activities?seasonId=${seasonId}`);

    return response.json();
  }

  public async fetchRecentActivities(
    skill: string,
    limit: number = 18,
  ): Promise<RecentActivity[]> {
    const response = await ApiService.get(
      `${this.activitiesBaseUrl}/recent`,
      {params: {skill, limit}},
    );

    return response.json();
  }

  public async postGeekActivityRequests(activityData: GrantActivityToMultipleGeeks): Promise<void> {
    await ApiService.post(`${this.activitiesBaseUrl}`, activityData);
  }

  public async fetchActivitiesHistory(
    filters: SearchFilter,
    offset: number = 0,
    limit: number = GEEKS_ACTIVITIES_HISTORY_LIMIT,
  ): Promise<{ data: ActivityHistory[], totalCount: number }> {
    const response = await ApiService.get(
      `${this.activitiesBaseUrl}/history`,
      {
        params: {
          offset,
          limit,
          reason: filters?.reason,
          requestedForId: filters.requestedForId,
          requestedById: filters.requestedById,
          activityTypeId: filters.activityTypeId,
          status: this.getActivityHistoryStatusType(filters.status),
        },
      },
    );
    const data: any[] = await response.json();
    const totalCount: number = parseInt(response.headers.get('x-items-count') as string, 10);

    return new Promise((resolve) => resolve({data, totalCount}));
  }

  public async deleteGeeksActivity(id: number): Promise<void> {
    await ApiService.delete(`${this.activitiesBaseUrl}/history/${id}`);
  }

  public async fetchWaitingActivityRequests(
    filters: SearchFilter,
  ): Promise<WaitingActivityRequests[]> {
    const response = await ApiService.get(
      `${this.activitiesBaseUrl}/status/waiting`,
      {
        params: {
          reason: filters?.reason,
          requestedForId: filters.requestedForId,
          requestedById: filters.requestedById,
          activityTypeId: filters.activityTypeId,
        },
      },
    );

    return response.json();
  }

  public async confirmWaitingActivityRequest(request: ResponseToActivityRequestsResponse): Promise<void> {
    await ApiService.put(`${this.activitiesBaseUrl}/requests/${request.id}/status/accepted`, request);
  }

  public async rejectWaitingActivityRequest(request: ResponseToActivityRequestsResponse): Promise<void> {
    await ApiService.put(`${this.activitiesBaseUrl}/requests/${request.id}/status/rejected`, request);
  }

  public async redirectWaitingActivityRequest(request: RedirectActivityRequestsResponse): Promise<void> {
    await ApiService.put(`${this.activitiesBaseUrl}/requests/${request.id}/status/redirected`,
      request);
  }

  private getActivityHistoryStatusType(status: ActivityHistoryStatusType): number {
    switch (status) {
      case ActivityHistoryStatusType.Unknown:
        return 0;
      case ActivityHistoryStatusType.Accepted:
        return 1;
      case ActivityHistoryStatusType.Rejected:
        return 2;
      case ActivityHistoryStatusType.AddedByAdmin:
        return 3;
    }
  }
}
